import { useCallback, useEffect, useRef, useState } from 'react'
import type { RtcError } from 'trtc-sdk-v5'

import type {
  LaunchContext,
  PreparedConversation,
  SubtitleLine,
  VoiceCallPhase,
} from '../types'
import { callApi, CallApiError } from '../services/callApi'
import {
  consumeMicrophoneTrack,
  prepareMicrophoneTrack,
  releaseMicrophoneTrack,
} from '../services/microphoneTrack'
import { AI_MESSAGE_CMD_ID, parseTRTCMessage } from '../services/trtcMessages'
import { returnToMiniProgram } from '../services/wechatMiniProgram'

type TRTCFactory = (typeof import('trtc-sdk-v5'))['default']
type TRTCClient = ReturnType<TRTCFactory['create']>
type ResumeAudio = () => Promise<void>

interface Handlers {
  error: (error: RtcError) => void
  kickedOut: () => void
  publish: (event: { state: string }) => void
  remoteEnter: (event: { userId: string }) => void
  remoteAudio: (event: { userId: string }) => void
  remoteExit: (event: { userId: string }) => void
  autoplay: (event: { mediaType: string; resume: ResumeAudio }) => void
  custom: (event: { userId: string; cmdId: number; data: ArrayBuffer }) => void
}

const HEARTBEAT_MS = 10_000
const CONNECT_TIMEOUT_MS = 20_000

function errorMessage(error: unknown): string {
  if (error instanceof CallApiError) return error.message
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return '麦克风权限未开启，请允许后重试'
  }
  return error instanceof Error ? error.message : '通话连接失败，请稍后重试'
}

export function useVoiceCall(launch: LaunchContext) {
  const [phase, setPhase] = useState<VoiceCallPhase>('ready')
  const [error, setError] = useState('')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [muted, setMuted] = useState(false)
  const [soundMuted, setSoundMuted] = useState(false)
  const [needsAudioResume, setNeedsAudioResume] = useState(false)
  const [subtitles, setSubtitles] = useState<SubtitleLine[]>([])
  const [aiState, setAiState] = useState<number>()
  const trtcRef = useRef<TRTCClient>()
  const trtcFactoryRef = useRef<TRTCFactory>()
  const handlersRef = useRef<Handlers>()
  const preparedRef = useRef<PreparedConversation>()
  const trackRef = useRef<MediaStreamTrack>()
  const attemptRef = useRef(0)
  const startRef = useRef<Promise<void>>()
  const endRef = useRef<Promise<void>>()
  const connectedAtRef = useRef(0)
  const remoteAudioRef = useRef(false)
  const publishResolveRef = useRef<() => void>()
  const resumeAudioRef = useRef<ResumeAudio>()

  const dispose = useCallback(async () => {
    const trtc = trtcRef.current
    const TRTC = trtcFactoryRef.current
    const handlers = handlersRef.current
    attemptRef.current += 1
    if (trtc && TRTC && handlers) {
      trtc.off(TRTC.EVENT.ERROR, handlers.error)
      trtc.off(TRTC.EVENT.KICKED_OUT, handlers.kickedOut)
      trtc.off(TRTC.EVENT.PUBLISH_STATE_CHANGED, handlers.publish)
      trtc.off(TRTC.EVENT.REMOTE_USER_ENTER, handlers.remoteEnter)
      trtc.off(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, handlers.remoteAudio)
      trtc.off(TRTC.EVENT.REMOTE_USER_EXIT, handlers.remoteExit)
      trtc.off(TRTC.EVENT.AUTOPLAY_FAILED, handlers.autoplay)
      trtc.off(TRTC.EVENT.CUSTOM_MESSAGE, handlers.custom)
    }
    trackRef.current?.stop()
    releaseMicrophoneTrack()
    if (trtc) {
      await trtc.exitRoom().catch(() => undefined)
      trtc.destroy()
    }
    trtcRef.current = undefined
    trtcFactoryRef.current = undefined
    handlersRef.current = undefined
    preparedRef.current = undefined
    trackRef.current = undefined
    connectedAtRef.current = 0
    remoteAudioRef.current = false
    resumeAudioRef.current = undefined
  }, [])

  const end = useCallback(
    async (reason = 'user_hangup', navigate = true) => {
      if (endRef.current) return endRef.current
      const promise = (async () => {
        setPhase('ending')
        await Promise.allSettled([callApi.stop(launch.id, reason), dispose()])
        setPhase('ended')
        if (navigate) await returnToMiniProgram(launch.id)
      })()
      endRef.current = promise
      try {
        await promise
      } finally {
        endRef.current = undefined
      }
    },
    [dispose, launch.id],
  )

  const start = useCallback(async () => {
    if (startRef.current) return startRef.current
    const promise = (async () => {
      const attempt = ++attemptRef.current
      setError('')
      setElapsedSeconds(0)
      setSubtitles([])
      try {
        setPhase('permission')
        const { default: TRTC } = await import('trtc-sdk-v5')
        trtcFactoryRef.current = TRTC
        const support = await TRTC.isSupported()
        if (!support.result) {
          setPhase('unsupported')
          setError('当前微信或系统暂不支持实时通话，可返回使用语音消息')
          return
        }
        await prepareMicrophoneTrack()
        const track = consumeMicrophoneTrack()
        if (!track) throw new Error('麦克风准备失败')
        trackRef.current = track
        if (attempt !== attemptRef.current) return

        setPhase('preparing')
        const prepared = await callApi.prepare(launch.id)
        preparedRef.current = prepared
        const trtc = TRTC.create()
        trtcRef.current = trtc

        const handlers: Handlers = {
          error: rtcError => {
            setError(errorMessage(rtcError))
            void end('client_error', false).then(() => setPhase('error'))
          },
          kickedOut: () => void end('client_error', false),
          publish: event => {
            if (event.state === 'started') publishResolveRef.current?.()
          },
          remoteEnter: event => {
            if (event.userId === prepared.botId) setPhase('waiting-bot')
          },
          remoteAudio: event => {
            if (event.userId !== prepared.botId) return
            remoteAudioRef.current = true
            connectedAtRef.current ||= Date.now()
            setPhase('connected')
          },
          remoteExit: event => {
            if (event.userId === prepared.botId) void end('client_error', false)
          },
          autoplay: event => {
            if (event.mediaType !== 'audio') return
            resumeAudioRef.current = event.resume
            setNeedsAudioResume(true)
          },
          custom: event => {
            if (event.userId !== prepared.botId || event.cmdId !== AI_MESSAGE_CMD_ID) return
            const message = parseTRTCMessage(event.data, prepared.userId)
            if (message?.state !== undefined) setAiState(message.state)
            if (!message?.subtitle) return
            setSubtitles(current => {
              const next = current.filter(line => line.key !== message.subtitle?.key)
              return [...next, message.subtitle!].slice(-4)
            })
          },
        }
        handlersRef.current = handlers
        trtc.on(TRTC.EVENT.ERROR, handlers.error)
        trtc.on(TRTC.EVENT.KICKED_OUT, handlers.kickedOut)
        trtc.on(TRTC.EVENT.PUBLISH_STATE_CHANGED, handlers.publish)
        trtc.on(TRTC.EVENT.REMOTE_USER_ENTER, handlers.remoteEnter)
        trtc.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, handlers.remoteAudio)
        trtc.on(TRTC.EVENT.REMOTE_USER_EXIT, handlers.remoteExit)
        trtc.on(TRTC.EVENT.AUTOPLAY_FAILED, handlers.autoplay)
        trtc.on(TRTC.EVENT.CUSTOM_MESSAGE, handlers.custom)

        setPhase('entering-room')
        await trtc.enterRoom({
          roomId: prepared.roomId,
          sdkAppId: prepared.sdkAppId,
          userId: prepared.userId,
          userSig: prepared.userSig,
          scene: TRTC.TYPE.SCENE_RTC,
          enableAutoPlayDialog: false,
        })
        const published = new Promise<void>((resolve, reject) => {
          publishResolveRef.current = resolve
          window.setTimeout(() => reject(new Error('麦克风连接超时')), CONNECT_TIMEOUT_MS)
        })
        await trtc.startLocalAudio({ option: { audioTrack: track } })
        await published
        publishResolveRef.current = undefined
        if (attempt !== attemptRef.current) return
        await callApi.start(launch.id)
        setPhase(remoteAudioRef.current ? 'connected' : 'waiting-bot')
      } catch (caught) {
        setError(errorMessage(caught))
        await Promise.allSettled([callApi.stop(launch.id, 'client_error'), dispose()])
        setPhase('error')
      }
    })()
    startRef.current = promise
    try {
      await promise
    } finally {
      startRef.current = undefined
    }
  }, [dispose, end, launch.id])

  const toggleMute = useCallback(async () => {
    const next = !muted
    await trtcRef.current?.updateLocalAudio({ mute: next })
    setMuted(next)
  }, [muted])

  const toggleSound = useCallback(async () => {
    const next = !soundMuted
    await trtcRef.current?.muteRemoteAudio('*', next)
    setSoundMuted(next)
  }, [soundMuted])

  const resumeAudio = useCallback(async () => {
    await resumeAudioRef.current?.()
    setNeedsAudioResume(false)
  }, [])

  useEffect(() => {
    if (!['preparing', 'entering-room', 'waiting-bot', 'connected'].includes(phase)) return
    const timer = window.setInterval(() => {
      const clientState = phase === 'connected' ? 'connected' : phase
      void callApi.heartbeat(launch.id, clientState).then(detail => {
        if (['finished', 'cancelled', 'failed', 'expired'].includes(detail.status)) {
          void end('client_error', false)
        }
      })
    }, HEARTBEAT_MS)
    return () => window.clearInterval(timer)
  }, [end, launch.id, phase])

  useEffect(() => {
    if (phase !== 'connected') return
    const tick = () => {
      const elapsed = Math.floor((Date.now() - connectedAtRef.current) / 1000)
      setElapsedSeconds(elapsed)
      if (elapsed >= launch.maxDurationSeconds) void end('duration_limit')
    }
    tick()
    const timer = window.setInterval(tick, 1_000)
    return () => window.clearInterval(timer)
  }, [end, launch.maxDurationSeconds, phase])

  useEffect(() => {
    const pageHide = () => {
      void callApi.stop(launch.id, 'page_hidden', true)
      void dispose()
    }
    window.addEventListener('pagehide', pageHide)
    return () => {
      window.removeEventListener('pagehide', pageHide)
      void dispose()
    }
  }, [dispose, launch.id])

  return {
    phase,
    error,
    elapsedSeconds,
    muted,
    soundMuted,
    needsAudioResume,
    subtitles,
    aiState,
    start,
    end,
    toggleMute,
    toggleSound,
    resumeAudio,
  }
}
